// Per-photo verdict review (ADR-0169) — an additive ANNOTATION layer on
// top of a checklist item's photos. The AI proposes, per attached photo,
// whether it shows the defect; the supervisor confirms each photo one by
// one. Nothing here ever feeds the item verdict, its severity, the score,
// the checklist snapshot, the PDF or `/r`: those are the signed acta and
// stay exactly as signed. The only state this layer can move is the live
// observation, and only through the regular lifecycle graph (auto-annul
// when every photo of the item was confirmed «conforme» by a human).
//
// Pure: no DB, no transport. core-api owns the tables; the wire derives
// its enums from the constants below (fitness: wire-enums-derive-from-engine).
import { z } from "zod";
import { legalObservationTargets } from "./observation-lifecycle.js";
/** What the AI may propose for one photo. «no_concluyente» = the photo
 *  does not let the model tell (blurry, wrong framing, unrelated). */
export const PHOTO_VERDICTS = ["defecto", "conforme", "no_concluyente"];
export const photoVerdictSchema = z.enum(PHOTO_VERDICTS);
/** What a human may confirm: a binary call. «no_concluyente» is a model
 *  outcome only — the supervisor always decides one way or the other. */
export const CONFIRMED_PHOTO_VERDICTS = ["defecto", "conforme"];
export const confirmedPhotoVerdictSchema = z.enum(CONFIRMED_PHOTO_VERDICTS);
/** Review phase of one photo. A confirmation wins over any proposal. */
export const PHOTO_REVIEW_PHASES = ["unreviewed", "proposed", "confirmed"];
/** Why an observation was annulled by this layer. One reason today. */
export const PHOTO_REVIEW_ANNULMENT_REASONS = ["all_photos_confirmed_conforme"];
export const photoReviewAnnulmentReasonSchema = z.enum(PHOTO_REVIEW_ANNULMENT_REASONS);
/** The note the auto-annul transition carries (destinations such as
 *  facility's «anulada» declare `requiresNote`). The store manager reads
 *  it in the annulment email, so it is user-facing es-CL copy. */
export const ALL_PHOTOS_CONFORME_ANNUL_NOTE = "Anulada: el supervisor revisó toda la evidencia y la confirmó conforme";
/** Photos per checklist item the gallery shows — and therefore the
 *  largest photo set a human can review end to end. A larger set never
 *  auto-annuls: the reviewer would be confirming a sample, not the
 *  evidence. core-api's GET /reports/:id/photos caps with this same
 *  constant so the two can't drift. */
export const PHOTOS_PER_ITEM = 3;
export function photoReviewPhase(proposal, confirmation) {
    if (confirmation)
        return "confirmed";
    if (proposal)
        return "proposed";
    return "unreviewed";
}
/** S(item): the item's captured photo URLs, deduplicated, in order. */
export function itemPhotoSet(capturedPhotoUrls) {
    return [...new Set(capturedPhotoUrls)];
}
/** The smallest photo set that is subject to per-photo review. */
export const MIN_PHOTOS_FOR_REVIEW = 2;
/**
 * Is the item subject to per-photo review at all? Founder decision
 * (ADR-0169 Postscript 2026-10-23): only when |S| ≥ 2. With exactly one
 * photo there is nothing to pick — that photo IS the evidence of the
 * item's verdict — so it gets no proposal shown, no confirmation and no
 * auto-annul path (the supervisor annuls manually, as before ADR-0169).
 * The item-level half of the rule; the card-level half (what the card
 * actually shows) is itemCardUnderPhotoReview, and both share the
 * threshold through photoCountRequiresReview.
 */
export function itemRequiresPhotoReview(photoSet) {
    return photoCountRequiresReview(itemPhotoSet(photoSet).length);
}
/** The threshold itself, on a bare count — for a surface that only has
 *  sizes on the wire (the dashboard's photoSetSize and card photo count). */
export function photoCountRequiresReview(distinctPhotos) {
    return distinctPhotos >= MIN_PHOTOS_FOR_REVIEW;
}
/**
 * Is the item's CARD under per-photo review? «Una sola foto» means one
 * photo on the card, not only |S| = 1 (ADR-0169 Postscript 2026-10-23):
 * the card can show fewer photos than S — a URL attached to two items is
 * ambiguous and lands on the unattributed card (photoVerdictIndex), and a
 * captured URL can be gone from area_captures.photo_urls. A card showing
 * a single photo of S is not reviewed either: there is nothing to pick,
 * and the photos of S it hides could never be confirmed, so the review
 * could never finish. Needs |S| ≥ 2 AND ≥ 2 distinct photos of S shown.
 * The photo-count half only: a surface decides with itemUnderPhotoReview,
 * which also requires the item to have an observation.
 */
export function itemCardUnderPhotoReview(photoSet, displayedUrls) {
    return itemUnderPhotoReview(true, photoSet, displayedUrls);
}
/**
 * THE rule for "is this item's card under per-photo review" — the single
 * source every surface reads (core-api's gallery, its review state, and
 * the dashboard's defensive check). Founder decision (ADR-0169 Postscript
 * 2026-10-23, second paragraph): the review exists to clear an
 * OBSERVATION, so it applies only to an item that materialized one — its
 * verdict was negative and the acta raised a finding from it. A conforming
 * item, an n/a or unreviewed item, and unattributed photos are never
 * under review, whatever the AI proposed for their photos (an AI
 * «defecto» on a conforming item's photo stays in the proposals history,
 * for evaluation tooling only). On top of that, the photo-count rules of
 * itemCardUnderPhotoReview: |S| ≥ 2 and ≥ 2 photos of S on the card.
 *
 * Counts, not URLs, so a surface with only sizes on the wire (the
 * dashboard: photoSetSize and the card's photo count) applies the very
 * same rule; itemUnderPhotoReview is the URL form core-api uses.
 */
export function photoReviewApplies(card) {
    return (card.hasObservation &&
        photoCountRequiresReview(card.distinctPhotoSet) &&
        photoCountRequiresReview(card.distinctShown));
}
/** photoReviewApplies from the item's captured URLs and the card's URLs. */
export function itemUnderPhotoReview(hasObservation, photoSet, displayedUrls) {
    const s = itemPhotoSet(photoSet);
    return photoReviewApplies({
        hasObservation,
        distinctPhotoSet: s.length,
        distinctShown: new Set(displayedUrls.filter((url) => s.includes(url))).size,
    });
}
/**
 * The URLs of S that do NOT yet have a latest human confirmation of
 * «conforme» — what stands between the item and an auto-annul.
 */
export function photosPendingConforme(photoSet, latestConfirmedByUrl) {
    return itemPhotoSet(photoSet).filter((url) => latestConfirmedByUrl.get(url) !== "conforme");
}
/**
 * True when a human confirmed EVERY photo of the item as «conforme».
 * Founder decisions (ADR-0169 §3 and its 2026-10-23 Postscript): only
 * when 2 ≤ |S| ≤ PHOTOS_PER_ITEM. An empty set never annuls (there is no
 * evidence to clear), a single photo is not under review
 * (itemRequiresPhotoReview) — a stored confirmation on it, from before
 * the Postscript, never annuls — and a set larger than the gallery cap
 * never annuls (part of it was never shown).
 */
export function itemPhotoSetAllConforme(photoSet, latestConfirmedByUrl) {
    const s = itemPhotoSet(photoSet);
    if (!itemRequiresPhotoReview(s) || s.length > PHOTOS_PER_ITEM)
        return false;
    return photosPendingConforme(s, latestConfirmedByUrl).length === 0;
}
/**
 * The status an auto-annul moves the observation to, read from the graph
 * and never from a key: the one terminal destination (`transitionsTo`
 * empty) the actor can legally reach from `from`. Null when there is
 * none — or more than one, which would make the choice a guess.
 * Facility, assigning_party, from «aceptada» or «asignada» → «anulada».
 */
export function photoReviewAnnulTarget(recipe, from, actor) {
    const terminal = legalObservationTargets(recipe, from, actor).filter((s) => s.terminal);
    return terminal.length === 1 ? terminal[0] : null;
}
//# sourceMappingURL=photo-review.js.map